import { z } from "zod";
import { initialWorkspaceState } from "../state/workspaceReducer";
import type { CartItem, WorkspaceState } from "./quotation";

export interface WorkspaceRestoreResult {
  state: WorkspaceState;
  warnings: string[];
}

export function deriveWorkspaceTitle(state: WorkspaceState): string {
  const customerLine = state.customerLines
    .split("\n")
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  if (customerLine) {
    return customerLine;
  }

  const firstItem = state.cart[0];
  if (firstItem) {
    return firstItem.description;
  }

  const selection = [state.selectedBrand, state.selectedCategory]
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(" ");
  return selection || "Untitled Quote";
}

export function restoreWorkspaceState(value: unknown): WorkspaceRestoreResult {
  const workspaceResult = workspaceBaseSchema.safeParse(value);
  if (!workspaceResult.success) {
    return {
      state: initialWorkspaceState,
      warnings: ["Saved workspace state was invalid and could not be restored."]
    };
  }

  const cart = restoreCartItems(workspaceResult.data.cart);
  const warnings: string[] = [];
  if (hasMalformedCart(value)) {
    warnings.push("Saved quote rows were invalid and could not be restored.");
  }
  if (cart.skippedRows > 0) {
    warnings.push(
      `${cart.skippedRows} saved quote row${
        cart.skippedRows === 1 ? "" : "s"
      } could not be restored and ${cart.skippedRows === 1 ? "was" : "were"} skipped.`
    );
  }

  return {
    state: {
      ...initialWorkspaceState,
      ...workspaceResult.data,
      cart: cart.items,
      comparison: restoreComparisonState(workspaceResult.data.comparison)
    },
    warnings
  };
}

function restoreCartItems(cart: unknown[]): { items: CartItem[]; skippedRows: number } {
  const items: CartItem[] = [];
  let skippedRows = 0;

  for (const row of cart) {
    const result = cartItemSchema.safeParse(row);
    if (result.success) {
      items.push(normalizeCartItem(result.data));
    } else {
      skippedRows += 1;
    }
  }

  return { items, skippedRows };
}

function hasMalformedCart(value: unknown): boolean {
  return isRecord(value) && "cart" in value && !Array.isArray(value.cart);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeCartItem(item: ParsedCartItem): CartItem {
  return {
    id: item.id,
    brand: item.brand,
    category: item.category,
    pricelistDisplayName: item.pricelistDisplayName,
    tag: item.tag,
    product: item.product,
    description: item.description,
    listPrice: item.listPrice,
    quantity: item.quantity,
    discount: item.discount,
    rate: item.rate,
    markup: item.markup,
    loadingFrls: item.loadingFrls,
    loadingPvc: item.loadingPvc,
    supportsFrls: item.supportsFrls,
    supportsPvc: item.supportsPvc,
    pricingMode: item.pricingMode
  };
}

function restoreComparisonState(value: unknown): WorkspaceState["comparison"] {
  const result = comparisonStateSchema.safeParse(value);
  if (!result.success) {
    return initialWorkspaceState.comparison;
  }

  return {
    brand: result.data.brand,
    rows: result.data.rows.map((row) => ({
      cartItemId: row.cartItemId,
      listPrice: row.listPrice,
      discount: row.discount,
      rate: row.rate,
      pricingMode: row.pricingMode,
      warning: row.warning
    }))
  };
}

const optionalNonNegativeNumberSchema = z
  .number()
  .finite()
  .min(0)
  .optional()
  .catch(undefined);

const optionalPercentSchema = z
  .number()
  .finite()
  .min(0)
  .max(100)
  .optional()
  .catch(undefined);

const workspaceBaseSchema = z.object({
  showPriceBreakdown: z.boolean().catch(false),
  documentType: z.enum(["quotation", "proforma"]).catch("quotation"),
  selectedBrand: z.string().catch(""),
  selectedCategory: z.string().catch(""),
  selectedPricelistTag: z.string().catch(""),
  globalDiscount: optionalPercentSchema,
  globalMarkup: optionalPercentSchema,
  deliveryCharges: optionalNonNegativeNumberSchema,
  customerLines: z.string().catch(""),
  termsText: z.string().catch(initialWorkspaceState.termsText),
  cart: z.array(z.unknown()).catch([]),
  comparison: z.unknown().catch(undefined)
});

const cartItemSchema = z.object({
  id: z.string().min(1),
  brand: z.string().min(1),
  category: z.string(),
  pricelistDisplayName: z.string(),
  tag: z.string().min(1),
  product: z.record(z.unknown()),
  description: z.string().min(1),
  listPrice: z.number().finite().min(0),
  quantity: optionalNonNegativeNumberSchema,
  discount: optionalPercentSchema,
  rate: optionalNonNegativeNumberSchema,
  markup: optionalPercentSchema,
  loadingFrls: z.boolean().catch(false),
  loadingPvc: z.boolean().catch(false),
  supportsFrls: z.boolean().catch(false),
  supportsPvc: z.boolean().catch(false),
  pricingMode: z.enum(["global", "row", "manual"]).optional().catch(undefined)
});

type ParsedCartItem = z.infer<typeof cartItemSchema>;

const comparisonRowSchema = z.object({
  cartItemId: z.string().min(1),
  listPrice: optionalNonNegativeNumberSchema,
  discount: optionalPercentSchema,
  rate: optionalNonNegativeNumberSchema,
  pricingMode: z.enum(["discount", "manual"]).catch("discount"),
  warning: z.string().optional().catch(undefined)
});

const comparisonStateSchema = z.object({
  brand: z.string().catch(""),
  rows: z.array(comparisonRowSchema).catch([])
});
